/**
 * Onboarding Widget
 * Dashboard widget for users who paused onboarding
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import { Rocket, Play, CheckCircle2, Circle, ChevronRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const OnboardingWidget = ({ onResume, className = '' }) => {
  const { user } = useAuth();
  const [showWidget, setShowWidget] = useState(false);
  const [status, setStatus] = useState(null);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    const checkStatus = async () => {
      if (!user?.id) return;
      
      try {
        const [shouldShowRes, stepsRes] = await Promise.all([
          axios.get(`${API}/onboarding/should-show?user_id=${user.id}`),
          axios.get(`${API}/onboarding/steps?user_id=${user.id}`)
        ]);
        
        const data = shouldShowRes.data;
        
        // Show widget if paused or not started (but not if wizard is showing or completed)
        if (
          (data.status === 'PAUSED' || data.status === 'NOT_STARTED') && 
          !data.should_show_wizard &&
          data.status !== 'COMPLETED'
        ) {
          setShowWidget(true);
          setStatus(data);
          setSteps(stepsRes.data);
        } else {
          setShowWidget(false);
        }
      } catch (error) {
        console.error('Error checking onboarding status:', error);
      }
    };
    
    checkStatus();
  }, [user?.id]);

  const handleResume = () => {
    if (onResume) {
      onResume();
    }
  };

  if (!showWidget || !status) {
    return null;
  }

  const completedCount = status.completed_count || 0;
  const totalSteps = status.total_steps || steps.length;
  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  return (
    <Card className={`${className}`} data-testid="onboarding-widget">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Rocket className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          Complete Your Setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress */}
        <div>
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{completedCount} of {totalSteps} steps</span>
          </div>
          <Progress value={progressPercent} className="h-2 [&>div]:bg-blue-500" />
        </div>

        {/* Next steps preview */}
        <div className="space-y-2">
          {steps.slice(completedCount, completedCount + 3).map((step, idx) => (
            <div 
              key={step.id}
              className={`flex items-center gap-2 text-sm p-2 rounded-lg ${
                idx === 0 
                  ? 'bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800' 
                  : ''
              }`}
            >
              {idx === 0 ? (
                <Circle className="h-4 w-4 text-blue-500 shrink-0" />
              ) : (
                <Circle className="h-4 w-4 text-slate-300 dark:text-slate-600 shrink-0" />
              )}
              <span className={idx === 0 ? 'font-medium' : 'text-muted-foreground'}>
                {step.title}
              </span>
              {idx === 0 && (
                <span className="text-xs text-blue-600 dark:text-blue-400 ml-auto">
                  Next
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Resume button */}
        <Button className="w-full" onClick={handleResume}>
          <Play className="h-4 w-4 mr-2" />
          Continue Setup
          <ChevronRight className="h-4 w-4 ml-auto" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default OnboardingWidget;
